import type { BlogCard } from './types';

export const childhoodFluVaccinationBlogCards: BlogCard[] = [
  {
    slug: 'childhood-flu-vaccination',
    metaTitle: 'Childhood Flu Vaccination From 6 Months Old | LIPS',
    contentType: 'article',
    title: {
      content:
        '### **Childhood flu vaccination:**\n\n## The Trivalent Flu Jab For Children From 6 Months Old',
      styles: {
        h3: 'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        h2: 'font-mulish font-bold text-royal-875 mb-3 text-base md:text-lg leading-tight',
        strong:
          'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        p: 'mb-0 mt-3',
      },
      topicsStyles: {
        h3: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 pt-2 font-black',
        h2: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 font-black leading-tight',
        strong: 'font-mulish text-text-subtitle font-bold',
      },
    },
    description: {
      content:
        "Childhood flu vaccination from 6 months: how the trivalent flu jab works, what happens on the day and common side effects. Book a children's flu jab at LIPS.",
      styles: {},
    },
    imageUrl: 'flu-jab-poster.webp',
    bannerUrl: 'flu-jab-poster.webp',
    bannerDescription: `Childhood flu vaccination helps protect children from flu and its complications, and children from 6 months old can have a flu jab given as a quick injection. At LIPS Healthcare, Battersea Power Station, children aged 6 months and over can have the trivalent flu injection, which is in stock this season. Below, we explain why the flu jab matters for children, how the trivalent vaccine works, what happens at the appointment and which side effects to look out for.

<blockquote class="border-l-4 border-theme-primary bg-surface-muted-section rounded-lg p-4 my-8 text-sm text-text-default not-italic">

<strong class="font-bold text-theme-primary">At a glance</strong>

- Children from 6 months old can have the flu injection.
- The trivalent flu jab protects against the three flu strains expected this season.
- Common side effects: a sore injection site, a mild temperature and tiredness.

</blockquote>
`,

    doctorSlug: 'yu-zhen-lau',
    iconUrl: 'yu-zhen-lau.webp',
    author: {
      content: 'Dr Yu Zhen Lau',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    speciality: 'GP - General Practice',
    date: {
      content: '28, October 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: { p: 'text-gray-500 text-xs' },
    },
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS',
        styles: {
          h3: 'font-mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'Why is childhood flu vaccination important?',
          id: 'why-is-childhood-flu-vaccination-important',
        },
        {
          text: 'What is the trivalent flu jab for children?',
          id: 'what-is-the-trivalent-flu-jab-for-children',
        },
        {
          text: 'Which children can have the flu injection?',
          id: 'which-children-can-have-the-flu-injection',
        },
        {
          text: "What happens at your child's flu jab appointment?",
          id: 'what-happens-at-your-childs-flu-jab-appointment',
        },
        {
          text: "Children's flu vaccine side effects",
          id: 'childrens-flu-vaccine-side-effects',
        },
        {
          text: 'Frequently asked questions',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `


### Why is childhood flu vaccination important?

Childhood flu vaccination is important because flu can make young children very unwell, and children also pass flu easily to brothers and sisters, classmates and older relatives.

Flu is not just a bad cold. In young children it can cause a high temperature, aching and exhaustion, and some children develop complications such as a chest infection. Children with long-term health conditions, such as asthma or a heart condition, are more likely to become seriously unwell.
<div align="center">\n\n
![flu-vaccination](flu-vaccination.webp "600x400")
</div>

Flu viruses change from year to year, so the vaccine is updated every season. That is why children need a flu jab each autumn, not just once. You can read more in the [NHS guidance on the children's flu vaccine](https://www.nhs.uk/vaccinations/child-flu-vaccine/).

### What is the trivalent flu jab for children?

The trivalent flu jab is an inactivated flu vaccine that protects against the three flu strains expected to circulate this season, and it is given as a single injection.

<div align="center">\n\n
![Trivalent flu vaccine](trivalent-flu.webp "600x400")
</div>

"Trivalent" simply means the vaccine covers three strains of flu. Because the vaccine is inactivated, it contains no live virus and cannot give your child flu. The strains included are chosen each year based on international surveillance, as set out in the [UKHSA Green Book](https://www.gov.uk/government/publications/influenza-the-green-book-chapter).

Some families avoid pork products for religious or personal reasons. If that applies to you, ask about the vaccine ingredients when you book, and the clinician will confirm them before your child has the jab.

> Protect your child this flu season. Book a children's flu jab for ages 6 months and over at LIPS Healthcare, Battersea Power Station.

### Which children can have the flu injection?

Children from 6 months old can have the trivalent flu injection, including children with long-term health conditions such as asthma, diabetes or heart problems.
<div align="center">\n\n
![flu-injection](flu-injection.webp "846x451")
</div>

Before giving the vaccine, a GMC-registered clinician will check your child's health and confirm whether the vaccine is suitable. If your child has a complex health condition, the LIPS Paediatrics team can advise alongside your GP. Let the clinician know if your child:

- has had a serious allergic reaction to a flu vaccine or any of its ingredients
- has a severe egg allergy, so the clinician can confirm the vaccine is suitable
- has a high temperature or is unwell on the day
- has a condition or takes a treatment that affects the immune system

Children under 9 who have never had a flu vaccine before may need a second dose at least four weeks after the first. Your clinician will tell you whether this applies. If your child is poorly on the day, ask the clinician whether it is fine to go ahead.


### What happens at your child's flu jab appointment?

A children's flu jab appointment is quick: the clinician checks your child's health, gives one injection and may ask you to wait for a few minutes afterwards.

1. The clinician asks about your child's health, allergies and any previous vaccines.
2. You hold or sit with your child to help them feel secure.
3. The injection is given into the thigh for babies and young toddlers, or the upper arm for older children.
4. You may be asked to wait briefly before leaving.
5. If a second dose is needed, you can book it before you go.

<div align="center">\n\n
![flu-appointment](flu-appointment.webp "846x451")
</div>

A few small things help nervous children. Be honest that it will feel like a quick scratch, bring a favourite toy, and plan a treat for afterwards. Most children are back to normal within minutes.

LIPS Healthcare is on the first floor of Turbine Hall B at Battersea Power Station, easy to reach by the Northern line or the London Overground. If you live in Clapham, Wandsworth or Nine Elms, the clinic is a short journey for an after-school or weekend appointment.


### Children's flu vaccine side effects

The most common children's flu vaccine side effects are a sore, red or swollen injection site, a mild temperature, irritability and tiredness, and these usually pass within a day or two.

Serious allergic reactions are rare. They usually happen within minutes of vaccination, which is why clinicians watch for any signs straight afterwards and are trained to respond.

<div align="center">\n\n
![flu-side-effects](flu-side-effects.webp "846x451")
</div>

If your child seems more unwell than you would expect, has a high temperature that does not settle, or you are worried for any reason, speak to a doctor. Families from Chelsea, Pimlico and Vauxhall can see a GP at LIPS, and for a problem that needs attention the same day but is not an emergency, the LIPS Urgent Care Centre can see your child.

> **Medical disclaimer**
>
> This article is for informational purposes only and does not constitute medical advice, diagnosis, or treatment. Always seek the advice of a qualified clinician for any medical condition. Source: NHS guidance on urgent care and walk-in centres, available at nhs.uk.

<span id="frequently-asked-questions" class="block scroll-mt-28"></span>
`,
      styles: {
        h3: 'text-xl md:text-2xl font-bold text-theme-primary mb-4 mt-8',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        strong: 'font-bold text-theme-primary',
        a: 'font-semibold text-teal-500 underline',
        li: 'text-sm md:text-base text-text-default leading-relaxed mb-2',
        ul: 'list-disc pl-6 mb-4 [&_li]:text-black [&_li]:font-bold',
        ol: 'list-decimal pl-6 mb-4 [&_li]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-muted-section rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
    },
    faq: {
      heading: {
        content: '### Frequently asked questions',
        styles: { h3: 'text-xl md:text-2xl font-bold text-theme-primary mb-6' },
      },
      items: [
        {
          question: { content: 'What age can a child have a flu jab?' },
          answer: {
            content:
              'From 6 months old. Children aged 6 months and over can have the trivalent flu injection, which LIPS Healthcare has in stock this season.',
          },
        },
        {
          question: { content: 'Does my child need a flu jab every year?' },
          answer: {
            content:
              'Yes. Flu viruses change and protection fades over time, so the vaccine is updated every season.',
          },
        },
        {
          question: {
            content: 'Does the children’s flu injection contain pork gelatine?',
          },
          answer: {
            content:
              'Ask before booking. Ingredients vary between flu vaccines, so the LIPS team will confirm the ingredients of the injection in stock if your family avoids pork products.',
          },
        },
        {
          question: { content: 'Can the flu jab give my child flu?' },
          answer: {
            content:
              'No. The trivalent flu injection is inactivated, so it contains no live virus and cannot cause flu.',
          },
        },
        {
          question: {
            content:
              'Is childhood flu vaccination safe alongside other vaccines?',
          },
          answer: {
            content:
              'Usually, yes. The flu jab can generally be given at the same time as other routine childhood vaccines, and your clinician will advise on timing.',
          },
        },
      ],
    },
    postFaqContent: {
      content: `
### **Sources**

- NHS: Flu vaccine: [https://www.nhs.uk/vaccinations/flu-vaccine/](https://www.nhs.uk/vaccinations/flu-vaccine/)
- UKHSA: Influenza, the Green Book, chapter 19 (inactivated vaccines from 6 months, two-dose schedule, egg allergy, co-administration): [https://www.gov.uk/government/publications/influenza-the-green-book-chapter-19](https://www.gov.uk/government/publications/influenza-the-green-book-chapter-19)
`,
      styles: {
        h3: 'text-xl md:text-2xl font-bold text-theme-primary mb-4 mt-8',
        strong: 'font-bold text-theme-primary',
        ul: 'list-disc pl-6 mb-4',
        li: 'text-sm md:text-base text-text-default leading-relaxed mb-2',
        a: 'font-semibold text-teal-500 underline break-words',
      },
    },
  },
];
